<script lang="ts">
    import { clickOutside } from "svelte-outside";
    import { LL } from "../../../i18n/i18n-svelte";
    import type { LockableAreaEntry } from "../../Stores/CurrentPlayerAreaLockStore";
    import LockIcon from "../Icons/meta/LockIcon.svelte";
    import LockOpenIcon from "../Icons/meta/LockOpenIcon.svelte";

    interface Props {
        areas: LockableAreaEntry[];
        areasWithPermission?: Set<string>;
        onselect?: (entry: LockableAreaEntry) => void;
        onclose?: () => void;
        /** When defined, show a row for the discussion bubble (group lock) in the list. */
        groupLockState?: boolean;
        /** Callback when the user selects the bubble row. Only used when groupLockState is defined. */
        onselectgrouplock?: () => void;
    }

    let {
        areas = [],
        areasWithPermission = new Set(),
        onselect,
        onclose,
        groupLockState,
        onselectgrouplock,
    }: Props = $props();

    function handleClose(): void {
        onclose?.();
    }

    function handleSelect(entry: LockableAreaEntry): void {
        onselect?.(entry);
        handleClose();
    }

    function handleSelectGroupLock(): void {
        onselectgrouplock?.();
        handleClose();
    }

    function entryKey(entry: LockableAreaEntry): string {
        return `${entry.areaId}:${entry.propertyId}`;
    }

    function canLock(entry: LockableAreaEntry): boolean {
        return areasWithPermission.has(entryKey(entry));
    }

    let showBubbleRow = $derived(groupLockState !== undefined && onselectgrouplock !== undefined);
</script>

<div
    data-testid="lockable-area-picker"
    class="bg-mlight/80 backdrop-blur-md rounded-md shadow-light p-2 max-w-96 overflow-auto flex flex-col gap-1"
    use:clickOutside={handleClose}
>
    <div class="text-sm font-semibold text-mdark px-2 pb-1">
        {$LL.actionbar.help.lock.areaPickerTitle()}
    </div>

    {#if showBubbleRow}
        <button
            type="button"
            class="w-full text-left p-2 rounded transition-colors flex flex-row items-center gap-2 {groupLockState
                ? 'bg-mred/10 hover:bg-mred/20'
                : 'hover:bg-mdark/10'}"
            data-testid="lockable-area-option-bubble"
            onclick={handleSelectGroupLock}
        >
            {#if groupLockState}
                <LockIcon strokeColor="stroke-mred" hover="" />
            {:else}
                <LockOpenIcon strokeColor="stroke-mdark" fillColor="fill-mdark" hover="" />
            {/if}
            <span class="text-sm text-mdark flex-1 truncate">
                {$LL.actionbar.help.lock.bubbleLabel()}
            </span>
        </button>
    {/if}

    {#each areas as entry, index (entryKey(entry))}
        <button
            type="button"
            class="w-full text-left p-2 rounded transition-colors flex flex-row items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed {entry.lockState
                ? 'bg-mred/10 hover:bg-mred/20'
                : 'hover:bg-mdark/10'}"
            data-testid="lockable-area-option-{index}"
            disabled={!canLock(entry)}
            onclick={() => canLock(entry) && handleSelect(entry)}
        >
            {#if entry.lockState}
                <LockIcon strokeColor="stroke-mred" hover="" />
            {:else}
                <LockOpenIcon strokeColor="stroke-mdark" fillColor="fill-mdark" hover="" />
            {/if}
            <span
                class="text-sm text-mdark flex-1 truncate"
                title={entry.areaName?.trim() || $LL.actionbar.help.lock.unnamedArea()}
            >
                {entry.areaName?.trim() || $LL.actionbar.help.lock.unnamedArea()}
            </span>
        </button>
    {/each}
</div>
